import { useCallback, useRef } from 'react'

// The page a signed-out visitor asked for, kept in this browser for a few hours (App.js's
// sign-in guard keeps it, and the experiment entry screen's Sign in again). The guard also
// hands it to /login as the router's `from`, but a sign-in that does not start from that
// redirect has no `from`: vizpi.org or /login typed, a new tab, the About page's Login. Those
// sign-ins went to the session list, which does not list a study's exercise, so a student who
// signed in that way could not get back to theirs. It is for the next sign-in only: the guard
// does not keep a page once someone has been signed in in that tab (a sign-out), and the
// first sign-in after uses it up wherever it goes (useAfterSignInPath) - the next person to
// sign in on this browser must not be sent to someone else's page.
const KEY = 'vzReturnPath'
export const RETURN_PATH_MAX_AGE_MS = 3 * 60 * 60 * 1000

// Not kept: the sign-in pages (a loop), and the bare session list - every sign-in ends there
// anyway, and it must not replace an exercise link kept earlier. Only paths of this app.
const NOT_KEPT = ['/', '/login', '/signup', '/session']
export const isKeepable = (path) =>
  typeof path === 'string' &&
  path.startsWith('/') &&
  !path.startsWith('//') &&
  !path.includes('\\') &&
  !NOT_KEPT.includes(path.replace(/\/+$/, '') || '/')

export const rememberReturnPath = (path) => {
  if (!isKeepable(path)) return
  try {
    localStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }))
  } catch (error) {
    // No storage (a private window): sign-ins go to the session list, as before.
  }
}

// The kept page while it is still valid, else null. Reading it leaves it kept.
export const rememberedReturnPath = () => {
  try {
    const kept = JSON.parse(localStorage.getItem(KEY))
    const age = Date.now() - Number(kept?.at)
    return isKeepable(kept?.path) && age >= 0 && age <= RETURN_PATH_MAX_AGE_MS ? kept.path : null
  } catch (error) {
    return null
  }
}

export const forgetReturnPath = () => {
  try {
    localStorage.removeItem(KEY)
  } catch (error) {
    // Nothing was kept.
  }
}

// An address the app has no page for that still carries a session's number - a link typed
// from the board with a typo (/sesion/955, /session955, /sessions/955; E1-S2, 2026-10-29) -
// is that session's page: /workspace/<n> when it says so, else /session/<n>, which sends a
// student on to /workspace when their exercise runs there. Without this they went to the
// session list, signed in there, and the number was gone. Null when it has no number (runs
// of more than 9 digits are not a session id).
export const sessionPathIn = (pathname) => {
  if (typeof pathname !== 'string') return null
  const number = (pathname.match(/\d+/g) || []).find((digits) => digits.length <= 9)
  if (!number) return null
  return `/${/work/i.test(pathname) ? 'workspace' : 'session'}/${Number(number)}`
}

// The kept page, forgotten as it is used: a sign-in goes there once.
export const takeReturnPath = () => {
  const path = rememberedReturnPath()
  if (path) forgetReturnPath()
  return path
}

// For Login and SignUp: where a visitor goes once signed in - `fromPath` (the page the sign-in
// guard sent them from) when it is a page to come back to (not the bare session list), else
// the kept page, else the session list. The kept page is taken by the first navigation after a
// sign-in even when `fromPath` wins, and at most once per visit to the sign-in page, so the
// navigations after one sign-in (Login's submit and its currentUser effect) agree. A stable
// function.
export const useAfterSignInPath = () => {
  const taken = useRef(undefined)
  return useCallback((fromPath) => {
    if (taken.current === undefined) taken.current = takeReturnPath()
    if (isKeepable(fromPath)) return fromPath
    return taken.current || '/session'
  }, [])
}
